/**
 * /components/:widgetId/pricing: which heat pump sizes the installer offers (a
 * switch per size) and the installed price for each, BEFORE the Boiler Upgrade
 * Scheme grant (worked out per home by the calculator, not chosen here).
 *
 * The widget sizes each home, picks the smallest switched-on size that is big
 * enough, takes off the grant and shows a range. A switched-off size is never
 * quoted but keeps its price, so switching it back on restores it.
 */
import { useEffect, useMemo, useState } from "react";
import { QUOTE_SPREAD, STANDARD_BUS_GRANT, UPLIFT_BUS_GRANT, hasPrices, normalisePricing } from "@widget";
import { useApi } from "../../api";
import { PriceEditor, priceProblems, toPriceState } from "../../components/PriceEditor";
import { useSaveBar, useTabDraft, useWidget } from "./WidgetLayout";
import { InstantQuotesCard } from "./InstantQuotesCard";

const gbp = (n: number) => `£${n.toLocaleString("en-GB")}`;

export function PricingPage() {
  const { widget, setWidget } = useWidget();
  const api = useApi();
  const saved = useMemo(() => normalisePricing(widget.pricing), [widget.pricing]);
  // Prices and the raw text per size (so half-typed values don't fight the number
  // parsing), kept by the layout so unsaved edits survive switching tabs
  const savedState = useMemo(() => toPriceState(saved), [saved]);
  const [state, setState, discardDraft] = useTabDraft("pricing", savedState);
  const draft = state.pricing;
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger"; text: string } | null>(null);

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  const problems = priceProblems(state);

  // Warn before leaving with unsaved prices
  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      setWidget(await api.updateWidget(widget.widgetId, { pricing: draft }));
      discardDraft();
      setMessage({ tone: "success", text: "Prices saved. New quotes use them straight away." });
    } catch (e) {
      setMessage({ tone: "danger", text: (e as Error).message });
    } finally {
      setSaving(false);
    }
  }

  function discard() {
    discardDraft();
    setMessage(null);
  }

  const onCount = draft.steps.filter((s) => s.enabled).length;

  // Save / Discard live in the widget header
  useSaveBar({ dirty, saving, blocked: problems > 0, onSave: save, onDiscard: discard });

  return (
    <div className="pricing">
      <section className="card">
        <h2>Your prices</h2>
        <p className="card-sub">
          Switch on the heat pump sizes you install and enter your full installed price for each, <strong>before</strong> the Boiler Upgrade
          Scheme grant.
        </p>

        {message && <div className={`flash flash-${message.tone}`}>{message.text}</div>}
        {problems > 0 && (
          <div className="flash flash-danger">
            {problems === 1 ? "1 size needs attention" : `${problems} sizes need attention`} before you can save.
          </div>
        )}
        {!hasPrices(saved) && (
          <div className="flash flash-info">
            {onCount === 0 ? "Switch on the sizes you install and add a price for each. " : ""}
            Until you save prices, the preview quotes with typical market prices (the grey placeholders).
          </div>
        )}

        <PriceEditor
          state={state}
          onChange={(next) => {
            setState(next);
            setMessage(null);
          }}
        />
      </section>

      <InstantQuotesCard />

      <section className="card pricing-explain">
        <h2>How the quote is worked out</h2>
        <ol>
          <li>We estimate the heat pump size the home needs from its EPC and the homeowner's answers.</li>
          <li>We pick the smallest size you've switched on that is big enough (or your largest size).</li>
          <li>
            We take off the Boiler Upgrade Scheme grant that home looks eligible for ({gbp(STANDARD_BUS_GRANT)}, or {gbp(UPLIFT_BUS_GRANT)} when
            it's replacing oil or LPG off the gas grid) and show a range of ±{Math.round(QUOTE_SPREAD * 100)}% around that price, rounded to
            £50.
          </li>
        </ol>
      </section>
    </div>
  );
}
